import { Button } from '@mantine/core';
import { useUnit } from 'effector-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { gameModel } from 'entities/game';
import { TURN_STATUSES } from 'shared/api';
import { IS_STEPBALL } from 'shared/config';
import { useBreakpoint, useHasScroll } from 'shared/lib';
import { NAVIGATION_IDS } from '../../config';
import { gameHelpModalModel } from '../../model';
import betMenuImg from '../assets/bet-menu.webp';
import calculatorImg from '../assets/calculator.webp';
import distributionImg from '../assets/distribution.webp';
import fastBetsImg from '../assets/fast-bets.webp';
import getImg from '../assets/get.webp';
import resultImg from '../assets/result.webp';
import stopAndWinningImg from '../assets/stop-and-winning.webp';
import cls from './game-help-modal-content.module.scss';

export const GameHelpModalContent = () => {
	const { t } = useTranslation();

	const [scrollCanChecked, status, autoChangeCurrentId] = useUnit([
		gameHelpModalModel.$scrollCanChecked,
		gameModel.$turnStatus,
		gameHelpModalModel.autoChangeCurrentId,
	]);

	const sm = useBreakpoint('sm', false);

	const { ref, isHas } = useHasScroll<HTMLDivElement>();

	useEffect(() => {
		const root = ref.current;
		if (!root) return () => {};

		const checkElementsPosition = () => {
			if (!scrollCanChecked) return;

			if (root.scrollTop === 0) {
				autoChangeCurrentId(NAVIGATION_IDS.HOW_TO_PLAY);

				return;
			}

			const rootRect = root.getBoundingClientRect();

			Object.values(NAVIGATION_IDS).forEach((id) => {
				const block = document.getElementById(id);

				if (!block) return;

				const blockRect = block.getBoundingClientRect();

				const topDiff = blockRect.top - rootRect.top;

				const bottomDiff = blockRect.bottom - rootRect.bottom;

				// if (Math.abs(topDiff) > rootRect.height) return;

				const isAtTop = Math.abs(topDiff) < 20;

				if (isAtTop || (topDiff < 0 && bottomDiff > 0)) autoChangeCurrentId(id);
			});
		};

		root.addEventListener('scroll', checkElementsPosition, { passive: true });

		return () => {
			root.removeEventListener('scroll', checkElementsPosition);
		};
	}, [scrollCanChecked]);

	const startOnboarding = useUnit(gameHelpModalModel.startOnboarding);

	return (
		<div
			ref={ref}
			data-scroll={isHas || undefined}
			className={cls.gameHelpModalContent}
		>
			<Button
				className={cls.gameHelpModalContentButton}
				disabled={status === TURN_STATUSES.bet}
				onClick={startOnboarding}
			>
				{t('game-help-modal.tutorial')}
			</Button>
			<div id={NAVIGATION_IDS.HOW_TO_PLAY}>
				<h3 className={cls.gameHelpModalContentTitle}>{t('game-help-modal.how-to-play.title')}</h3>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.how-to-play.welcome', {
						game: IS_STEPBALL ? 'StepBall' : 'ChezaChini',
					})}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.how-to-play.introduction')}
				</p>
			</div>
			<div id={NAVIGATION_IDS.CELL_DISTRIBUTION_BY_ROW}>
				<h3 className={cls.gameHelpModalContentTitle}>
					{t('game-help-modal.cell-distribution-by-row.title')}
				</h3>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.cell-distribution-by-row.row-1-4')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.cell-distribution-by-row.row-5-7')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.cell-distribution-by-row.row-8-9')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.cell-distribution-by-row.row-10')}
				</p>
				<img
					src={distributionImg}
					alt='distribution'
					width='50%'
					className={cls.gameHelpModalContentImg}
				/>
			</div>
			<div id={NAVIGATION_IDS.STOP_AND_WINNING}>
				<h3 className={cls.gameHelpModalContentTitle}>
					{t('game-help-modal.stop-and-winning.title')}
				</h3>
				<img
					src={stopAndWinningImg}
					alt='stop-and-winning'
					className={cls.gameHelpModalContentImg}
				/>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.stop-and-winning.description-1')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.stop-and-winning.description-2')}
				</p>
				<img
					src={getImg}
					alt='get'
					width={186}
					className={cls.gameHelpModalContentImg}
				/>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.stop-and-winning.description-3')}
				</p>
				<img
					src={resultImg}
					alt='result'
					width='50%'
					className={cls.gameHelpModalContentImg}
				/>
			</div>
			<div id={NAVIGATION_IDS.PLACING_BETS}>
				<h3 className={cls.gameHelpModalContentTitle}>{t('game-help-modal.placing-bets.title')}</h3>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.placing-bets.description')}
				</p>
			</div>
			{!sm ? (
				<div id={NAVIGATION_IDS.BET_CALCULATOR}>
					<h3 className={cls.gameHelpModalContentTitle}>
						{t('game-help-modal.bet-calculator.title')}
					</h3>
					<p className={cls.gameHelpModalContentParagraph}>
						{t('game-help-modal.bet-calculator.description')}
					</p>
					<img
						src={calculatorImg}
						alt='calculator'
						width='50%'
						className={cls.gameHelpModalContentImg}
					/>
					<p className={cls.gameHelpModalContentParagraph}>
						{t('game-help-modal.bet-calculator.fast-bets-description')}
					</p>
					<p className={cls.gameHelpModalContentParagraph}>
						<b>{t('game-help-modal.fast-bets-and-x2-toggle.fast-bets')}</b>:&nbsp;
						{t('game-help-modal.fast-bets-and-x2-toggle.fast-bets-description')}
					</p>
				</div>
			) : (
				<div id={NAVIGATION_IDS.FAST_BETS_AND_X2_TOGGLE}>
					<h3 className={cls.gameHelpModalContentTitle}>
						{t('game-help-modal.fast-bets-and-x2-toggle.title')}
					</h3>
					<p className={cls.gameHelpModalContentParagraph}>
						{t('game-help-modal.fast-bets-and-x2-toggle.description')}
					</p>
					<p className={cls.gameHelpModalContentParagraph}>
						<b>{t('game-help-modal.fast-bets-and-x2-toggle.fast-bets')}</b>:&nbsp;
						{t('game-help-modal.fast-bets-and-x2-toggle.fast-bets-description')}
					</p>
					<img
						src={fastBetsImg}
						alt='fast-bets'
						width='50%'
						className={cls.gameHelpModalContentImg}
					/>
					<p className={cls.gameHelpModalContentParagraph}>
						<b>{t('game-help-modal.fast-bets-and-x2-toggle.x2')}</b>:&nbsp;
						{t('game-help-modal.fast-bets-and-x2-toggle.x2-description')}
					</p>
					<img
						src={betMenuImg}
						alt='bet-menu'
						width='50%'
						className={cls.gameHelpModalContentImg}
					/>
				</div>
			)}
			<div id='music-and-sounds'>
				<h3 className={cls.gameHelpModalContentTitle}>
					{t('game-help-modal.music-and-sounds.title')}
				</h3>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.music-and-sounds.description')}
				</p>
			</div>
			<div id={NAVIGATION_IDS.RTP}>
				<h3 className={cls.gameHelpModalContentTitle}>{t('game-help-modal.rtp.title')}</h3>
				<p className={cls.gameHelpModalContentParagraph}>{t('game-help-modal.rtp.description')}</p>
			</div>
			<div id={NAVIGATION_IDS.DISCONNECTION_POLICY}>
				<h3 className={cls.gameHelpModalContentTitle}>
					{t('game-help-modal.disconnection-policy.title')}
				</h3>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.disconnection-policy.description')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					<b>{t('game-help-modal.disconnection-policy.players-side')}</b>
					{t('game-help-modal.disconnection-policy.players-side-description')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					<b>{t('game-help-modal.disconnection-policy.our-side')}</b>
					{t('game-help-modal.disconnection-policy.our-side-description')}
				</p>
				<p className={cls.gameHelpModalContentParagraph}>
					{t('game-help-modal.disconnection-policy.note')}
				</p>
			</div>
		</div>
	);
};
